import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getGuidanceForScores } from '../../../services/documentService';
import { REPS_COLORS } from '../utils/constants';

/** REPS quadrant full labels */
const QUADRANT_LABELS = {
  reliability: 'Reliability',
  efficiency: 'Efficiency',
  productivity: 'Productivity',
  safety: 'Safety',
};

/**
 * Rank guidance items by relevance to the user's lowest scores.
 * Items targeting areas where the user scores lowest appear first.
 *
 * @param {Array} guidanceItems - Array of guidance content documents
 * @param {Object} scores - Map of quadrant key to score value
 * @returns {Array} Sorted guidance items (most relevant first)
 */
function rankByLowestScores(guidanceItems, scores) {
  if (!scores || !guidanceItems.length) return guidanceItems;

  // Find the lowest-scoring quadrants
  const sortedQuadrants = Object.entries(scores)
    .filter(([, v]) => typeof v === 'number')
    .sort(([, a], [, b]) => a - b);

  // Build a priority map: lowest quadrant = highest priority (lowest number)
  const priorityMap = {};
  sortedQuadrants.forEach(([key], idx) => {
    priorityMap[key] = idx;
  });

  return [...guidanceItems].sort((a, b) => {
    // Score range relevance: items whose scoreRange targets lower scores rank higher
    const aRelevance = getRelevanceScore(a, priorityMap, scores);
    const bRelevance = getRelevanceScore(b, priorityMap, scores);
    if (aRelevance !== bRelevance) return aRelevance - bRelevance;

    // Tie-break on explicit priority field
    return (a.priority || 99) - (b.priority || 99);
  });
}

/**
 * Calculate a relevance score for a guidance item.
 * Lower = more relevant (targets the user's weakest areas).
 */
function getRelevanceScore(item, priorityMap, scores) {
  const ranges = item.scoreRanges || item.scoreRange;
  if (!ranges || typeof ranges !== 'object') return 50; // neutral

  // If it's a single scoreRange (min/max), check all quadrants
  if ('min' in ranges && 'max' in ranges) {
    // Find which quadrant this principleOrElement maps to — use lowest score quadrant
    let bestPriority = 50;
    for (const [quadrant, priority] of Object.entries(priorityMap)) {
      const score = scores[quadrant];
      if (score != null && score >= (ranges.min ?? 0) && score <= (ranges.max ?? 10)) {
        bestPriority = Math.min(bestPriority, priority);
      }
    }
    return bestPriority;
  }

  // If it's keyed by quadrant (scoreRanges map)
  let bestPriority = 50;
  for (const [quadrant, range] of Object.entries(ranges)) {
    const priority = priorityMap[quadrant];
    if (priority != null && range) {
      const score = scores[quadrant];
      if (score != null) {
        const min = typeof range.min === 'number' ? range.min : 0;
        const max = typeof range.max === 'number' ? range.max : 10;
        if (score >= min && score <= max) {
          bestPriority = Math.min(bestPriority, priority);
        }
      }
    }
  }
  return bestPriority;
}

/**
 * Determine which quadrant(s) a guidance item relates to based on the user's scores.
 */
function getMatchedQuadrants(item, scores) {
  const matched = [];
  const ranges = item.scoreRanges || item.scoreRange;

  if (!ranges || !scores) return matched;

  if ('min' in ranges && 'max' in ranges) {
    // Single score range — match against all quadrants
    for (const [quadrant, score] of Object.entries(scores)) {
      if (typeof score !== 'number') continue;
      if (score >= (ranges.min ?? 0) && score <= (ranges.max ?? 10)) {
        matched.push(quadrant);
      }
    }
  } else {
    // Map keyed by quadrant
    for (const [quadrant, range] of Object.entries(ranges)) {
      if (!range || scores[quadrant] == null) continue;
      const min = typeof range.min === 'number' ? range.min : 0;
      const max = typeof range.max === 'number' ? range.max : 10;
      if (scores[quadrant] >= min && scores[quadrant] <= max) {
        matched.push(quadrant);
      }
    }
  }
  return matched;
}

/**
 * GuidancePanel — personalized guidance recommendations based on assessment scores.
 *
 * Queries Hugo_GuidanceContent by assessment type, ranks by user's lowest scores,
 * and displays top 3-5 recommendations with action steps.
 *
 * Props:
 *   assessmentType - 'cvsa' | 'drift_accumulation' | etc.
 *   scores - Map of score keys to values (e.g. { reliability: 3, efficiency: 7, ... })
 *   maxItems - Maximum recommendations to display (default 5)
 *   title - Optional section title override
 */
export default function GuidancePanel({
  assessmentType = 'drift_accumulation',
  scores,
  maxItems = 5,
  title = 'Personalized Guidance',
}) {
  const [guidance, setGuidance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Fetch guidance content when assessmentType or scores change
  useEffect(() => {
    if (!scores || Object.keys(scores).length === 0) {
      setGuidance([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getGuidanceForScores(assessmentType, scores)
      .then((items) => {
        if (!cancelled) {
          setGuidance(items);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('GuidancePanel: failed to fetch guidance', err);
          setError(err.message || 'Failed to load guidance content');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assessmentType, scores]);

  // Rank and limit guidance items
  const rankedGuidance = useMemo(() => {
    const ranked = rankByLowestScores(guidance, scores);
    return ranked.slice(0, maxItems);
  }, [guidance, scores, maxItems]);

  // Find the lowest scoring quadrant for context display
  const lowestQuadrant = useMemo(() => {
    if (!scores) return null;
    let lowest = null;
    let lowestScore = Infinity;
    for (const [key, val] of Object.entries(scores)) {
      if (typeof val === 'number' && val < lowestScore) {
        lowestScore = val;
        lowest = key;
      }
    }
    return lowest ? { key: lowest, score: lowestScore, label: QUADRANT_LABELS[lowest] } : null;
  }, [scores]);

  const handleAccordionChange = (id) => (_event, isExpanded) => {
    setExpandedId(isExpanded ? id : null);
  };

  // --- Loading state ---
  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LightbulbIcon color="warning" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LightbulbIcon color="warning" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Unable to load guidance: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // --- Empty state ---
  if (rankedGuidance.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LightbulbIcon color="warning" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No guidance recommendations available yet.
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Guidance content will appear here as it becomes available for your assessment type.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LightbulbIcon color="warning" />
          <Typography variant="h6">{title}</Typography>
        </Box>

        {lowestQuadrant && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <TrendingDownIcon fontSize="small" color="error" />
            <Typography variant="body2" color="text.secondary">
              Focus area:{' '}
              <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: REPS_COLORS[lowestQuadrant.key] || 'text.primary' }}>
                {lowestQuadrant.label}
              </Typography>
              {' '}(score: {lowestQuadrant.score.toFixed(1)}/10)
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Top {rankedGuidance.length} recommendation{rankedGuidance.length !== 1 ? 's' : ''} based on your assessment scores:
        </Typography>

        {/* Guidance cards as expandable accordions */}
        <Stack spacing={1}>
          {rankedGuidance.map((item, idx) => {
            const matchedQuadrants = getMatchedQuadrants(item, scores);

            return (
              <Accordion
                key={item.id || idx}
                expanded={expandedId === (item.id || idx)}
                onChange={handleAccordionChange(item.id || idx)}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 56,
                    '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1.5 },
                  }}
                >
                  {/* Rank number */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </Box>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {/* Virtue/element name */}
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                      {item.title || item.virtueOrMetric || item.principleOrElement || 'Recommendation'}
                    </Typography>
                    {/* Brief description preview */}
                    {item.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: '90%' }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Matched quadrant chips */}
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {matchedQuadrants.map((q) => (
                      <Chip
                        key={q}
                        label={QUADRANT_LABELS[q] || q}
                        size="small"
                        sx={{
                          bgcolor: REPS_COLORS[q] || '#666',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    ))}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0 }}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Full description */}
                  {item.description && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {item.description}
                    </Typography>
                  )}

                  {/* Score context */}
                  {matchedQuadrants.length > 0 && scores && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Score Context
                      </Typography>
                      <Stack spacing={1}>
                        {matchedQuadrants.map((q) => {
                          const score = scores[q];
                          if (score == null) return null;
                          const pct = Math.min((score / 10) * 100, 100);
                          return (
                            <Box key={q}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {QUADRANT_LABELS[q]}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: REPS_COLORS[q] }}>
                                  {score.toFixed(1)}/10
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: REPS_COLORS[q] || 'primary.main',
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Action steps */}
                  {item.actionSteps && item.actionSteps.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Action Steps
                      </Typography>
                      <List dense disablePadding>
                        {item.actionSteps.map((step, stepIdx) => (
                          <ListItem key={stepIdx} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                              <CheckCircleOutlineIcon
                                sx={{ fontSize: 16, color: 'success.main' }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={typeof step === 'string' ? step : step.text || `Step ${step.stepNumber || stepIdx + 1}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Quote */}
                  {item.quote && item.quote.text && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.light',
                        display: 'flex',
                        gap: 1,
                      }}
                    >
                      <FormatQuoteIcon sx={{ color: 'primary.light', flexShrink: 0, mt: -0.5 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          {item.quote.text}
                        </Typography>
                        {item.quote.attribution && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            — {item.quote.attribution}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
